import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, TrendingUp, TrendingDown, AlertTriangle, DollarSign, BarChart3, Users, Loader2, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tabGuides = {
  pipeline: {
    title: 'Pipeline Health Score',
    purpose: 'Monitors the health of each open position by analyzing how actively it is being worked on (resume flow, interview scheduling, and closures relative to days open).',
    deliverables: ['Health score (0-100) for each open position', 'Color-coded health status', 'Resume, interview, and closure counts per position'],
    howToRead: [
      { label: 'Score 60-100 (Green)', color: 'bg-emerald-500', desc: 'Position is well-staffed and progressing on time. Good candidate pipeline.' },
      { label: 'Score 30-59 (Amber)', color: 'bg-amber-500', desc: 'Needs attention — stalled interviews, low resumes, or slow progress.' },
      { label: 'Score 0-29 (Red)', color: 'bg-red-500', desc: 'At risk — likely no activity, stuck pipeline, or very overdue. Requires immediate action.' },
    ],
    reference: 'Score is calculated based on: (resumes × 2 + interviews × 3 + closures × 5) relative to days open. Older positions without progress score lower.',
  },
  forecast: {
    title: 'Recruiter Performance Forecast',
    purpose: 'Projects future recruiter performance based on their last 3 months of activity, so managers can anticipate output and plan workloads.',
    deliverables: ['3-month historical resume/closure data per recruiter', 'Projected resumes and closures for next month', 'Trend direction (up/down)'],
    howToRead: [
      { label: 'Trend Up (Green Arrow)', color: 'bg-emerald-500', desc: 'Recruiter\'s output is increasing month-over-month. Positive trajectory.' },
      { label: 'Trend Down (Red Arrow)', color: 'bg-red-500', desc: 'Recruiter\'s output is declining. May need support, training, or workload rebalancing.' },
      { label: 'Projected R/C', color: 'bg-blue-500', desc: 'Projected Resumes and Closures are based on weighted moving average of recent months.' },
    ],
    reference: 'Projections use a recency-weighted average (recent months carry more weight). Not a guarantee — intended as a planning guide.',
  },
  demand: {
    title: 'Demand Surge Alerts',
    purpose: 'Detects when a client suddenly starts hiring significantly more than usual, alerting you to capitalize on the opportunity or prepare resources.',
    deliverables: ['Client-wise position surge detection', 'Severity rating (high/medium)', 'Comparison: recent 30 days vs previous 30 days'],
    howToRead: [
      { label: 'High Severity (Red)', color: 'bg-red-500', desc: 'Major spike — client has doubled or more their hiring. Prioritize this account.' },
      { label: 'Medium Severity (Amber)', color: 'bg-amber-500', desc: 'Noticeable increase in hiring. Worth monitoring and proactive outreach.' },
      { label: '+N Increase', color: 'bg-blue-500', desc: 'The number of additional positions opened compared to the previous period.' },
    ],
    reference: 'Alert triggers: +3 positions and 2x increase = Medium; +5 positions and 3x increase = High.',
  },
  revenue: {
    title: 'Revenue Forecast',
    purpose: 'Projects future revenue based on historical patterns, helping leadership plan budgets and set realistic targets.',
    deliverables: ['6-month revenue history (bar chart)', '3-month revenue projection with confidence level', 'Average monthly revenue calculation'],
    howToRead: [
      { label: 'Bar Height', color: 'bg-blue-500', desc: 'Represents actual revenue earned that month from successful closures/joinings.' },
      { label: 'Green Bars (Projected)', color: 'bg-emerald-500', desc: 'Estimated future revenue based on weighted historical trends.' },
      { label: 'Confidence %', color: 'bg-slate-400', desc: 'How reliable the projection is. 80%+ = very reliable. Below 60% = treat with caution.' },
    ],
    reference: 'Projections based on 3-month weighted moving average. Confidence decreases for months further out. Revenue in INR.',
  },
};

function InfoGuide({ guide, open, toggle }) {
  if (!guide) return null;
  return (
    <div className="mb-4" data-testid="info-guide">
      <button onClick={toggle} className="flex items-center gap-2 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors">
        <HelpCircle size={14} />{open ? 'Hide Guide' : 'How to read this'}{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-3 animate-in slide-in-from-top-2 duration-200">
          <div>
            <p className="font-bold text-blue-900 mb-1">Purpose</p>
            <p className="text-slate-600 text-xs">{guide.purpose}</p>
          </div>
          <div>
            <p className="font-bold text-blue-900 mb-1">What You Get</p>
            <ul className="space-y-0.5">
              {guide.deliverables.map((d, i) => <li key={`del-${i}`} className="text-xs text-slate-600 flex items-start gap-1.5"><span className="mt-1 w-1 h-1 bg-blue-400 rounded-full flex-shrink-0" />{d}</li>)}
            </ul>
          </div>
          <div>
            <p className="font-bold text-blue-900 mb-1">Color / Rating Reference</p>
            <div className="space-y-1.5">
              {guide.howToRead.map((h, i) => (
                <div key={`tip-${i}`} className="flex items-start gap-2">
                  <span className={`w-3 h-3 rounded-full ${h.color} flex-shrink-0 mt-0.5`} />
                  <div><span className="font-semibold text-blue-900 text-xs">{h.label}:</span> <span className="text-slate-600 text-xs">{h.desc}</span></div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-slate-400 italic border-t border-blue-200 pt-2">{guide.reference}</p>
        </div>
      )}
    </div>
  );
}

const tabs = [
  { id: 'pipeline', label: 'Pipeline Health', icon: Activity },
  { id: 'forecast', label: 'Recruiter Forecast', icon: Users },
  { id: 'demand', label: 'Demand Surge', icon: AlertTriangle },
  { id: 'revenue', label: 'Revenue Forecast', icon: DollarSign },
];

function HealthBadge({ health }) {
  const colors = { green: 'bg-emerald-100 text-emerald-800', amber: 'bg-amber-100 text-amber-800', red: 'bg-red-100 text-red-800' };
  return <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${colors[health] || 'bg-slate-100 text-slate-600'}`}>{health}</span>;
}

function ScoreBar({ score }) {
  const color = score >= 60 ? 'bg-emerald-500' : score >= 30 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-bold text-slate-600 w-8 text-right">{score}</span>
    </div>
  );
}

function PipelineHealth() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/nextmove/pipeline-health`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data?.positions || r.data || []))
      .catch(e => toast.error('Failed to load pipeline health'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  const summary = { red: data.filter(d => d.health === 'red').length, amber: data.filter(d => d.health === 'amber').length, green: data.filter(d => d.health === 'green').length };

  return (
    <div data-testid="pipeline-health-section">
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-extrabold text-red-700">{summary.red}</p>
          <p className="text-xs font-bold text-red-600 uppercase">At Risk</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-extrabold text-amber-700">{summary.amber}</p>
          <p className="text-xs font-bold text-amber-600 uppercase">Needs Attention</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-extrabold text-emerald-700">{summary.green}</p>
          <p className="text-xs font-bold text-emerald-600 uppercase">On Track</p>
        </div>
      </div>

      {data.length === 0 ? (
        <p className="text-center text-slate-400 py-8">No open positions to analyze</p>
      ) : (
        <div className="space-y-2">
          {data.map((p) => (
            <div key={p.position_id || p.title} data-testid={`pipeline-row-${p.position_id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-blue-900 truncate">{p.title}</p>
                <p className="text-xs text-slate-500">{p.company || p.client} &middot; {p.days_open}d open</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-600">
                <span title="Resumes"><BarChart3 size={12} className="inline mr-0.5" />{p.resumes}</span>
                <span title="Interviews"><Users size={12} className="inline mr-0.5" />{p.interviews}</span>
                <span title="Closures"><TrendingUp size={12} className="inline mr-0.5" />{p.closures}</span>
              </div>
              <div className="w-28"><ScoreBar score={p.score} /></div>
              <HealthBadge health={p.health} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecruiterForecast() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/nextmove/recruiter-forecast`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data?.recruiters || r.data || []))
      .catch(e => toast.error('Failed to load forecast'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="recruiter-forecast-section">
      {data.length === 0 ? (
        <p className="text-center text-slate-400 py-8">No recruiter data available</p>
      ) : (
        <div className="space-y-3">
          {data.map((r) => (
            <div key={r.recruiter_id || r.id || r.name} data-testid={`forecast-row-${r.name}`} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">{r.name?.charAt(0)}</div>
                  <span className="font-bold text-blue-900">{r.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  {r.trend === 'up' ? <TrendingUp size={16} className="text-emerald-600" /> : <TrendingDown size={16} className="text-red-500" />}
                  <span className={`text-xs font-bold uppercase ${r.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>{r.trend}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-slate-400">Closures (3m)</p>
                  <p className="font-bold text-blue-900">{r.recent_closures_3m || 0}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-slate-400">Joinings (3m)</p>
                  <p className="font-bold text-blue-900">{r.recent_joinings_3m || 0}</p>
                </div>
                <div className="bg-blue-50 rounded p-2 border border-blue-200">
                  <p className="text-blue-500 font-bold">Proj. Closures</p>
                  <p className="font-extrabold text-blue-800">{r.projected_closures_next_month || r.projected_closures || 0}</p>
                </div>
                <div className="bg-emerald-50 rounded p-2 border border-emerald-200">
                  <p className="text-emerald-500 font-bold">Proj. Joinings</p>
                  <p className="font-extrabold text-emerald-800">{r.projected_joinings_next_month || r.projected_joinings || 0}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DemandSurge() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/nextmove/demand-surge`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data?.alerts || r.data || []))
      .catch(e => toast.error('Failed to load demand alerts'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="demand-surge-section">
      {data.length === 0 ? (
        <div className="text-center py-12">
          <AlertTriangle size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-400 font-semibold">No demand surges detected</p>
          <p className="text-xs text-slate-400">Alerts appear when a client's hiring rate spikes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((a) => {
            // Tolerate legacy field names (a.client / a.recent_positions / a.previous_positions / a.increase)
            // while preferring the canonical backend shape from nextmove.demand_surge.
            const client = a.company_name || a.client || 'Unknown';
            const recent = a.positions_last_30d ?? a.recent_positions ?? 0;
            const previous = a.positions_prev_30d ?? a.previous_positions ?? 0;
            const surge = a.surge_ratio != null ? `${a.surge_ratio}×` : (a.increase != null ? `+${a.increase}` : '—');
            const sev = a.severity || 'info';
            const isHot = sev === 'critical' || sev === 'high';
            return (
              <div key={a.company_id || a.client || client} data-testid={`surge-row-${client}`}
                   className={`border rounded-lg p-4 flex items-center gap-4 ${isHot ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <AlertTriangle size={24} className={isHot ? 'text-red-600' : 'text-amber-600'} />
                <div className="flex-1">
                  <p className="font-bold text-blue-900">{client}</p>
                  <p className="text-xs text-slate-600">{recent} positions (last 30d) vs {previous} (prev 30d)</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-extrabold text-blue-900">{surge}</p>
                  <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${isHot ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>{sev}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RevenueForecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/nextmove/revenue-forecast`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const d = r.data || {};
        setData({
          history: d.historical || d.history || [],
          forecasts: d.projections || d.forecasts || [],
          avg_monthly: d.summary?.avg_monthly_revenue || d.avg_monthly || 0
        });
      })
      .catch(e => toast.error('Failed to load revenue forecast'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data) return <p className="text-center text-slate-400 py-8">No revenue data</p>;

  const maxRev = Math.max(...[...(data.history || []).map(h => h.revenue), ...(data.forecasts || []).map(f => f.projected_revenue)], 1);

  return (
    <div data-testid="revenue-forecast-section">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 text-center">
        <p className="text-xs font-bold text-blue-600 uppercase">Avg Monthly Revenue (Last 3 months)</p>
        <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-blue-900">{(data.avg_monthly || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
      </div>

      <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-3">Historical Revenue</p>
      <div className="flex items-end gap-2 h-32 mb-6">
        {(data.history || []).map((m) => (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-xs font-bold text-slate-600">{(m.revenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
            <div className="w-full bg-blue-500 rounded-t" style={{ height: `${Math.max(4, (m.revenue / maxRev) * 100)}%` }} />
            <span className="text-[10px] text-slate-500">{m.month}</span>
          </div>
        ))}
      </div>

      <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-3">Projected Revenue (Next 3 Months)</p>
      <div className="space-y-2">
        {(data.forecasts || []).map((f) => (
          <div key={f.month} data-testid={`forecast-month-${f.month}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-4">
            <span className="text-sm font-bold text-blue-900 w-20">{f.month}</span>
            <div className="flex-1">
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(f.projected_revenue / maxRev) * 100}%` }} />
              </div>
            </div>
            <span className="text-sm font-extrabold text-blue-900 w-28 text-right">{(f.projected_revenue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</span>
            <span className="text-xs text-slate-500 w-16 text-right">
              {(f.projected_revenue || 0) > 0 ? `${Math.round(Number(f.confidence) || 0)}% conf` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 size={32} className="animate-spin text-blue-400" />
    </div>
  );
}

export default function NextMove() {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [guideOpen, setGuideOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const visibleTabs = user?.role === 'admin_support'
    ? tabs.filter(t => t.id !== 'revenue')
    : tabs;

  return (
    <div data-testid="nextmove-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Activity className="inline-block mr-2 mb-1" size={32} />NextMove
        </h1>
        <p className="text-slate-500 text-sm">Predictive analytics for smarter recruitment decisions</p>
      </div>

      {/* Tab Selector */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4" data-testid="nextmove-tabs">
        {visibleTabs.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => { setActiveTab(t.id); setGuideOpen(false); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-all ${activeTab === t.id ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-blue-700'}`}
          >
            <t.icon size={16} />{t.label}
          </button>
        ))}
      </div>

      {/* Guide */}
      <InfoGuide guide={tabGuides[activeTab]} open={guideOpen} toggle={() => setGuideOpen(!guideOpen)} />

      {/* Tab Content */}
      {activeTab === 'pipeline' && <PipelineHealth />}
      {activeTab === 'forecast' && <RecruiterForecast />}
      {activeTab === 'demand' && <DemandSurge />}
      {activeTab === 'revenue' && <RevenueForecast />}
    </div>
  );
}
